// ============================================================================
// RESTORAN YARATISH (provisioning) — so'rovni tayyorlash
//
// Platforma paneli formadagi tanlovlardan bitta "spec" (JSON) yig'adi.
// Server (app.provision_tenant) uni BITTA tranzaksiyada qo'llaydi va
// har bir qiymatni yana o'zi tekshiradi — bu yerdagi tekshiruv faqat
// foydalanuvchiga tez va tushunarli xato ko'rsatish uchun.
// ============================================================================

import { LIMIT_UNITS } from './billing';
import { validateModules } from './modules';
import { getRoleTemplate } from './roles';
import type { RoleTemplate } from './roles';
import { getSetting, validateSettingValue } from './settings';
import type { SettingDef } from './settings';
import { LIBRARY_GROUPS, libraryCategoryImageKey, libraryGroupKey } from './menu-library';
import { STARTER_LIBRARY, starterForTemplate } from './starter-content';
import type { StarterContent, StarterRecipe } from './starter-content';
import { getRestaurantTemplate, isTemplateAvailable } from './templates';
import type { RestaurantTemplate } from './templates';

export const PROVISION_SPEC_VERSION = 1;
export const OWNER_EMAIL_DOMAIN = 'owners.onepos.uz';
export const DEFAULT_TRIAL_DAYS = 14;
export const DEFAULT_HALL_TABLES = 8;

// ---------------------------------------------------------------------------
// Telefon raqami: ega shu raqam va parol bilan kiradi
// ---------------------------------------------------------------------------

/** "+998 90 123-45-67", "901234567", "998901234567" -> "+998901234567". Noto'g'ri bo'lsa null. */
export function normalizePhone(raw: string): string | null {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 9) digits = `998${digits}`;
  return /^998\d{9}$/.test(digits) ? `+${digits}` : null;
}

/** Ega kirishi uchun ichki email: telefon raqamidan. Foydalanuvchi uni ko'rmaydi. */
export function ownerLoginEmail(phone: string): string {
  const normalized = normalizePhone(phone);
  if (!normalized) throw new Error('Telefon raqami noto‘g‘ri');
  return `${normalized.slice(1)}@${OWNER_EMAIL_DOMAIN}`;
}

// ---------------------------------------------------------------------------
// Shablon -> sozlamalar
// ---------------------------------------------------------------------------

/** Shablondagi ish modeli sozlamalar sifatida (restoran uchun aniq yoziladi) */
export function templateSettings(t: RestaurantTemplate): Record<string, unknown> {
  return {
    'flow.order_taker': t.flow.orderTaker,
    'flow.payment_timing': t.flow.paymentTiming,
    'flow.kitchen_routing': t.flow.kitchenRouting,
    'flow.order_types': [...t.flow.orderTypes],
    'flow.service_charge_percent': t.flow.serviceChargePercent,
    'pos.ask_order_type': t.flow.orderTypes.length > 1,
    ...(t.settings ?? {}),
  };
}

// ---------------------------------------------------------------------------
// Kirish ma'lumotlari (forma)
// ---------------------------------------------------------------------------

export type ContentChoice = { mode: 'template' } | { mode: 'empty' } | { mode: 'copy'; fromTenantId: string };

export interface NewRestaurantInput {
  name: string;
  city?: string;
  ownerName: string;
  ownerPhone: string;
  planId: string;
  templateKey: string;
  branchName?: string;
  branchAddress?: string;
  /** Sinov muddati, kun (0–90). Standart 14 */
  trialDays?: number;
  /** Muddat tugab, muhlat o'tgach avtomatik to'xtatish (Q-064). Standart: yo'q */
  autoSuspend?: boolean;
  /** Berilmasa — shablondagi modullar */
  modules?: readonly string[];
  /** Tarifdan tashqari yoqiladigan bo'limlar (Q-081; narxi bo'lsa — oylik summaga qo'shiladi) */
  addonModules?: readonly string[];
  /** Tarifdagidan ortiq qurilmalar soni: { pos: 1, waiter: 5 } (Q-081) */
  extraDevices?: Readonly<Record<string, number>>;
  /** Shablondan farqli sozlamalar */
  settings?: Readonly<Record<string, unknown>>;
  content: ContentChoice;
  /** Zal moduli yoqiq bo'lsa, nechta stol. Standart 8 */
  hallTables?: number;
  /** Ega PIN'i (6 raqam). Berilmasa server yaratadi va bir marta ko'rsatadi */
  ownerPin?: string;
}

// ---------------------------------------------------------------------------
// Spec (serverga yuboriladigan JSON). Kalitlar bazadagi kabi snake_case.
// ---------------------------------------------------------------------------

export interface SpecRole {
  key: string;
  name: string;
  description: string;
  template_key: string;
  locked: boolean;
  pin_length: 4 | 6;
  device_kinds: string[];
  permissions: { key: string; limit: number | null }[];
}

export interface SpecProduct {
  name: string;
  /** Kutubxona taomi (rasm, Q-054) */
  library_key: string | null;
  /** Guruh rasmi kaliti — faqat kutubxonada shunday guruh bo'lsa */
  group_key: string | null;
  short_name: string | null;
  price: number;
  group_name: string | null;
  variant_label: string | null;
  prep_minutes: number;
  track_stock: boolean;
  recipe: Record<string, number>;
  modifier_groups: string[];
}

export interface SpecMenu {
  mode: 'template';
  key: string;
  raw_materials: {
    code: string;
    name: string;
    category: string;
    base_unit: string;
    pack_name: string | null;
    pack_size: number | null;
    min_stock: number;
  }[];
  modifier_groups: {
    key: string;
    name: string;
    min_select: number;
    max_select: number;
    modifiers: { name: string; price: number; is_default: boolean; recipe: Record<string, number> }[];
  }[];
  categories: {
    name: string;
    /** Kategoriya fon rasmi kaliti: "cat_lavash" (Q-054) */
    library_key: string | null;
    icon: string | null;
    station: string | null;
    sort_order: number;
    modifier_groups: string[];
    products: SpecProduct[];
  }[];
}

export interface ProvisionSpec {
  version: number;
  tenant: {
    name: string;
    city: string | null;
    owner_name: string;
    owner_phone: string;
    plan_id: string;
    template_key: string;
    template_version: number;
    trial_days: number;
    auto_suspend: boolean;
  };
  branch: { code: string; name: string; address: string | null };
  modules: string[];
  settings: Record<string, unknown>;
  stations: { key: string; name: string; output: string }[];
  roles: SpecRole[];
  owner: { full_name: string; pin: string | null };
  content: SpecMenu | { mode: 'empty' } | { mode: 'copy'; from_tenant: string };
  hall: { zones: { name: string; tables: { name: string; seats: number }[] }[] } | null;
  /** Tarifdan tashqari (Q-081). Bo'lmasa — kalit yo'q */
  addons?: { modules: string[]; extra: Record<string, number> };
}

export type BuildResult = { ok: true; spec: ProvisionSpec } | { ok: false; errors: string[] };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function roleToSpec(r: RoleTemplate): SpecRole {
  return {
    key: r.key,
    name: r.label,
    description: r.description,
    template_key: r.key,
    locked: r.locked === true,
    pin_length: r.pinLength,
    device_kinds: [...r.deviceKinds],
    // Ega roli qulflangan: server unga doim barcha huquqlarni beradi
    permissions: r.locked
      ? []
      : r.permissions.map((key) => ({ key, limit: r.limits?.[key] ?? null })),
  };
}

function recipeFor(recipe: StarterRecipe | undefined, withStock: boolean): Record<string, number> {
  return withStock && recipe ? { ...recipe } : {};
}

const LIBRARY_GROUP_KEYS: ReadonlySet<string> = new Set(LIBRARY_GROUPS.map((g) => g.key));

export function menuToSpec(c: StarterContent, stationKeys: ReadonlySet<string>, withStock: boolean): SpecMenu {
  const lib = STARTER_LIBRARY[c.key];
  return {
    mode: 'template',
    key: c.key,
    raw_materials: withStock
      ? c.rawMaterials.map((r) => ({
          code: r.code,
          name: r.name,
          category: r.category,
          base_unit: r.baseUnit,
          pack_name: r.packName ?? null,
          pack_size: r.packSize ?? null,
          min_stock: r.minStock ?? 0,
        }))
      : [],
    modifier_groups: c.modifierGroups.map((g) => ({
      key: g.key,
      name: g.name,
      min_select: g.minSelect,
      max_select: g.maxSelect,
      modifiers: g.modifiers.map((m) => ({
        name: m.name,
        price: m.price,
        is_default: m.isDefault === true,
        recipe: recipeFor(m.recipe, withStock),
      })),
    })),
    categories: c.categories.map((cat, i) => ({
      name: cat.name,
      library_key: lib?.categories[cat.name] ? libraryCategoryImageKey(lib.categories[cat.name]!) : null,
      icon: cat.icon ?? null,
      station: cat.station && stationKeys.has(cat.station) ? cat.station : null,
      sort_order: i + 1,
      modifier_groups: [...(cat.modifierGroups ?? [])],
      products: cat.products.map((p) => ({
        name: p.name,
        library_key: lib?.products[p.name] ?? null,
        group_key: p.group && LIBRARY_GROUP_KEYS.has(libraryGroupKey(p.group)) ? libraryGroupKey(p.group) : null,
        short_name: p.shortName ?? null,
        price: p.price,
        group_name: p.group ?? null,
        variant_label: p.variant ?? null,
        prep_minutes: p.prepMinutes ?? 5,
        track_stock: withStock && (p.trackStock ?? true) && Object.keys(p.recipe ?? {}).length > 0,
        recipe: recipeFor(p.recipe, withStock),
        modifier_groups: [...(p.modifierGroups ?? [])],
      })),
    })),
  };
}

/** Formadan serverga yuboriladigan spec. Xato bo'lsa — foydalanuvchiga tushunarli ro'yxat. */
export function buildProvisionSpec(input: NewRestaurantInput): BuildResult {
  const errors: string[] = [];

  const name = input.name.trim();
  if (name.length < 1 || name.length > 120) errors.push('Restoran nomi 1–120 belgi bo‘lishi kerak');
  const ownerName = input.ownerName.trim();
  if (ownerName.length < 1 || ownerName.length > 120) errors.push('Ega ismi kiritilishi kerak');
  const phone = normalizePhone(input.ownerPhone);
  if (!phone) errors.push('Telefon raqami noto‘g‘ri (+998 XX XXX XX XX)');
  if (!/^[a-z0-9_]+$/.test(input.planId)) errors.push('Tarif tanlanmagan');

  const template = getRestaurantTemplate(input.templateKey);
  if (!template) {
    errors.push('Ish modeli (shablon) tanlanmagan');
    return { ok: false, errors };
  }
  if (!isTemplateAvailable(template)) errors.push(`“${template.label}” shabloni hali tayyor emas`);

  const trialDays = input.trialDays ?? DEFAULT_TRIAL_DAYS;
  if (!Number.isInteger(trialDays) || trialDays < 0 || trialDays > 180) errors.push('Sinov muddati 0–180 kun');

  const modules = [...new Set(input.modules ?? template.modules)];
  const addonModules = [...new Set(input.addonModules ?? [])].filter((m) => !modules.includes(m)).sort();
  errors.push(...validateModules([...modules, ...addonModules]));
  const has = (m: string) => modules.includes(m) || addonModules.includes(m);

  const extra: Record<string, number> = {};
  for (const [k, v] of Object.entries(input.extraDevices ?? {})) {
    if (!(k in LIMIT_UNITS)) errors.push(`Noma’lum qurilma: ${k}`);
    else if (!Number.isInteger(v) || v < 0 || v > 999) errors.push(`Qo‘shimcha ${LIMIT_UNITS[k]}: 0–999`);
    else if (v > 0) extra[k] = v;
  }

  // Sozlamalar: shablon + o'zgartirishlar
  const settings: Record<string, unknown> = { ...templateSettings(template), ...(input.settings ?? {}) };
  for (const [key, value] of Object.entries(settings)) {
    const def: SettingDef | undefined = getSetting(key);
    if (!def) {
      errors.push(`Noma’lum sozlama: ${key}`);
      continue;
    }
    const err = validateSettingValue(def, value);
    if (err) errors.push(`${def.label}: ${err}`);
  }

  if (input.ownerPin !== undefined && !/^\d{6}$/.test(input.ownerPin)) errors.push('Ega PIN’i 6 ta raqam bo‘lishi kerak');

  let content: ProvisionSpec['content'];
  const stationKeys = new Set(template.stations.map((s) => s.key));
  if (input.content.mode === 'template') {
    const starter = starterForTemplate(template.key);
    content = starter ? menuToSpec(starter, stationKeys, has('inventory')) : { mode: 'empty' };
  } else if (input.content.mode === 'copy') {
    if (!UUID_RE.test(input.content.fromTenantId)) errors.push('Nusxa olinadigan restoran tanlanmagan');
    content = { mode: 'copy', from_tenant: input.content.fromTenantId };
  } else {
    content = { mode: 'empty' };
  }

  const tables = has('hall') ? (input.hallTables ?? DEFAULT_HALL_TABLES) : 0;
  if (!Number.isInteger(tables) || tables < 0 || tables > 200) errors.push('Stollar soni 0–200');


  const roles = template.roles.map((k) => {
    const r = getRoleTemplate(k);
    if (!r) throw new Error(`Rol shabloni yo‘q: ${k}`);
    return roleToSpec(r);
  });

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    spec: {
      version: PROVISION_SPEC_VERSION,
      tenant: {
        name,
        city: input.city?.trim() || null,
        owner_name: ownerName,
        owner_phone: phone!,
        plan_id: input.planId,
        template_key: template.key,
        template_version: template.version,
        trial_days: trialDays,
        auto_suspend: input.autoSuspend === true,
      },
      branch: {
        code: 'B1',
        name: input.branchName?.trim() || name,
        address: input.branchAddress?.trim() || null,
      },
      modules,
      settings,
      stations: template.stations.map((s) => ({ key: s.key, name: s.label, output: s.output })),
      roles,
      owner: { full_name: ownerName, pin: input.ownerPin ?? null },
      content,
      hall:
        tables > 0
          ? {
              zones: [
                {
                  name: 'Asosiy zal',
                  tables: Array.from({ length: tables }, (_, i) => ({ name: `Stol ${i + 1}`, seats: 4 })),
                },
              ],
            }
          : null,
      ...(addonModules.length > 0 || Object.keys(extra).length > 0 ? { addons: { modules: addonModules, extra } } : {}),
    },
  };
}

// ---------------------------------------------------------------------------
// Baza testlari uchun: har bir tayyor shablondan haqiqiy spec.
// supabase/tests/_provision_specs.sql shu funksiya natijasi bilan bir xil
// bo'lishi kerak (tests/provision-specs-sync.test.ts tekshiradi).
// ---------------------------------------------------------------------------
export function provisionSpecsSqlFixture(templateKeys: readonly string[]): string {
  const lines = [
    '-- AVTOMATIK YARATILGAN — qo‘lda o‘zgartirmang.',
    '-- Manba: packages/shared/src/provisioning.ts (provisionSpecsSqlFixture).',
    '-- Yangilash: tests/provision-specs-sync.test.ts xato xabaridagi matnni shu faylga qo‘ying.',
    'create table if not exists test.specs (template text primary key, spec jsonb not null);',
    'truncate test.specs;',
  ];
  templateKeys.forEach((key, i) => {
    const result = buildProvisionSpec({
      name: `Sinov ${key}`,
      city: 'Toshkent',
      ownerName: `Ega ${key}`,
      ownerPhone: `+99890100000${i}`,
      planId: 'pro',
      templateKey: key,
      content: { mode: 'template' },
    });
    if (!result.ok) throw new Error(`${key}: ${result.errors.join('; ')}`);
    const json = JSON.stringify(result.spec).replace(/'/g, "''");
    lines.push(`insert into test.specs values ('${key}', '${json}');`);
  });
  return `${lines.join('\n')}\n`;
}
